import { useState } from "react";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";

/* ============================================================
   COMO ADICIONAR AS FOTOS REAIS
   Basta colocar os arquivos na pasta /public/images/enceradeira/
   com os nomes exatos usados abaixo (ex: servico-1.jpg, antes-1.jpg).
   Enquanto o arquivo não existir, aparece um placeholder automático
   no lugar — nada quebra e nada mais precisa ser alterado no código.
   ============================================================ */

const WHATSAPP_NUMBER = "5561982574468";
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Olá, vim pelo site e gostaria de um orçamento para minha enceradeira industrial",
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

const colors = {
  navy: "#0A1628",
  navyLight: "#0F2040",
  blue: "#1565C0",
  blueLight: "#1E88E5",
  blueBright: "#42A5F5",
  accent: "#00B4D8",
  accentLight: "#48CAE4",
  white: "#FFFFFF",
  offWhite: "#F0F4FA",
  lightGray: "#E8EEF8",
  midGray: "#8FA3C8",
  dark: "#06101E",
  cardBg: "#0D1E35",
  glass: "rgba(255,255,255,0.05)",
  glassHover: "rgba(255,255,255,0.1)",
};

/* ---------- Ícones SVG puro (mesmo padrão do Header/Footer) ---------- */

const IconImg = ({ size = 28, color = colors.midGray }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);

const IconDroplet = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2.69l5.66 5.66a8 8 0 11-11.32 0z" />
  </svg>
);

const IconShield = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
  </svg>
);

const IconClock = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 16 14" />
  </svg>
);

const IconPlug = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22v-5" />
    <path d="M9 8V2M15 8V2" />
    <path d="M7 8h10v4a5 5 0 01-10 0z" />
  </svg>
);

const IconGauge = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 14l3-3" />
    <path d="M3.34 19a10 10 0 1117.32 0" />
  </svg>
);

const IconHome = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 10.5L12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
  </svg>
);

const IconUsers = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20a6.5 6.5 0 0113 0" />
    <circle cx="17.5" cy="9.5" r="2.6" />
    <path d="M15.5 13a5.5 5.5 0 015.7 7" />
  </svg>
);

const IconBrush = ({ size = 24, color = colors.accent }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 114.03 4.03l-8.06 8.08" />
    <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 00-3-3.02z" />
  </svg>
);

const IconArrowRight = ({ size = 20, color = colors.white }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const IconWhatsApp = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ---------- Componente de imagem com fallback automático ---------- */

const PhotoBox = ({
  src,
  alt,
  ratio = "4 / 3",
  label,
  position = "center",
}) => {
  const [broken, setBroken] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: ratio,
        borderRadius: 14,
        overflow: "hidden",
        background: colors.cardBg,
        border: `1px solid rgba(255,255,255,0.07)`,
      }}
    >
      {!broken ? (
        <img
          src={src}
          alt={alt}
          onError={() => setBroken(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: position,
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            background: `linear-gradient(135deg, ${colors.navyLight}, ${colors.cardBg})`,
            padding: 12,
            textAlign: "center",
          }}
        >
          <IconImg size={28} />
          <span
            style={{ fontSize: 11.5, color: colors.midGray, lineHeight: 1.4 }}
          >
            {label || "Foto em breve"}
          </span>
        </div>
      )}
    </div>
  );
};

/* ---------- Conteúdo ---------- */

const dicas = [
  {
    icon: IconDroplet,
    titulo: "Use produtos compatíveis com o piso",
    texto:
      "Ceras, resinas e produtos de limpeza incorretos aceleram o desgaste do disco e podem manchar o piso. Siga sempre a recomendação do fabricante do piso e da enceradeira.",
  },
  {
    icon: IconBrush,
    titulo: "Verifique escovas e discos regularmente",
    texto:
      "Escovas e discos desgastados reduzem o rendimento da enceradeira industrial e sobrecarregam o motor. Inspecione antes de cada uso e troque assim que notar desgaste irregular.",
  },
  {
    icon: IconPlug,
    titulo: "Cheque cabo e fiação antes de ligar",
    texto:
      "Cabos danificados são a causa mais comum de curto-circuito em enceradeira industrial. Verifique fios expostos, plugue e extensão antes de conectar o equipamento.",
  },
  {
    icon: IconGauge,
    titulo: "Ajuste a pressão ao tipo de piso",
    texto:
      "Pressão excessiva desgasta o piso e o motor; pressão insuficiente compromete o resultado do polimento. Regule conforme o tipo de piso: mármore, granito, porcelanato ou cimento queimado.",
  },
  {
    icon: IconClock,
    titulo: "Respeite o tempo de uso contínuo",
    texto:
      "Motores de enceradeira industrial têm ciclo de trabalho recomendado pelo fabricante. Uso contínuo além do limite superaquece o motor e reduz a vida útil do equipamento.",
  },
  {
    icon: IconShield,
    titulo: "Guarde em local seco e coberto",
    texto:
      "Umidade é inimiga do motor elétrico e das partes metálicas. Armazene a enceradeira industrial em local arejado, longe de água e sol direto, sempre com o cabo enrolado sem dobras.",
  },
  {
    icon: IconUsers,
    titulo: "Treine a equipe de limpeza",
    texto:
      "Boa parte dos defeitos vem do uso incorreto por operadores não treinados. Um treinamento básico de operação evita quebras e aumenta a vida útil do equipamento.",
  },
  {
    icon: IconHome,
    titulo: "Faça manutenção preventiva periódica",
    texto:
      "Revisões preventivas a cada 3 a 6 meses ajudam a identificar desgastes em rolamentos, platinados, fios e outros componentes. A identificação antecipada de defeitos permite realizar a manutenção preventiva e evitar falhas inesperadas e reparos mais caros.",
  },
];

const fotosServicos = [
  {
    src: "/images/enceradeira/caixa-de-engrenagem-enceradeira-industrial.jpeg",
    label: "Caixa de engrenagem",
    position: "center",
  },
  {
    src: "/images/enceradeira/motor-de-enceradeira-industrial.jpeg",
    label: "Diagnótico de motor elétrico queimado",
    position: "center",
  },
  {
    src: "/images/enceradeira/enceradeiras-revitalizadas.jpeg",
    label: "Manutenção e revitalização de enceradeiras",
    position: "center",
  },
  {
    src: "/images/enceradeira/suportes-para-enceradeira.jpeg",
    label: "Reposição de suportes para disco",
    position: "center",
  },
];

const antesDepois = [
  {
    titulo: "Enceradeiras revisadas enviadas para São Paulo",
    antes: "/images/enceradeira/enceradeira-antes.jpeg",
    antesPos: "center",
    depois: "/images/enceradeira/enceradeiras-schuller.jpeg",
    depoisPos: "center",
  },
  {
    titulo: "Enceradeira industrial — motor recondicionado",
    antes: "/images/enceradeira/antes-2.jpeg",
    antesPos: "center",
    depois: "/images/enceradeira/depois-2.jpeg",
    depoisPos: "center",
  },
];

const PaginaEnceradeira = () => {
  return (
    <div style={{ background: colors.dark, minHeight: "100vh" }}>
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        style={{
          background: `linear-gradient(180deg, ${colors.navy}, ${colors.dark})`,
          padding: "160px 24px 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            left: "-10%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colors.blue}22, transparent 70%)`,
            filter: "blur(70px)",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              borderRadius: 999,
              background: colors.glass,
              border: `1px solid rgba(0,180,216,0.25)`,
              marginBottom: 22,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: colors.accent,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: colors.accentLight,
              }}
            >
              ASSISTÊNCIA TÉCNICA ESPECIALIZADA EM BRASÍLIA!
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(30px, 5vw, 48px)",
              lineHeight: 1.15,
              color: colors.white,
              maxWidth: 820,
              margin: 0,
            }}
          >
            Conserto e Assistência Técnica de{" "}
            <span
              style={{
                background: `linear-gradient(135deg, ${colors.blueBright}, ${colors.accent})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Enceradeira
            </span>{" "}
            em Brasília
          </h1>

          <p
            style={{
              color: colors.midGray,
              fontSize: 17,
              lineHeight: 1.75,
              maxWidth: 760,
              marginTop: 22,
            }}
          >
            Especializada em{" "}
            <strong style={{ color: colors.lightGray }}>
              manutenção preventiva e corretiva de enceradeira Cleaner, Deep
              CLean, Certec, Romher, entre outras marcas.
            </strong>{" "}
            Atendimento rápido e com garantia para hospitais, clínicas,
            condomínios e empresas de limpeza no DF.
          </p>

          <div
            style={{
              marginTop: 32,
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                background: `linear-gradient(135deg, ${colors.blue}, ${colors.accent})`,
                color: colors.white,
                textDecoration: "none",
                padding: "14px 26px",
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <IconWhatsApp size={18} /> Solicitar orçamento
            </a>
            <a
              href="#dicas"
              style={{
                color: colors.lightGray,
                textDecoration: "none",
                padding: "14px 26px",
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                border: `1px solid rgba(255,255,255,0.15)`,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              Ver dicas de uso <IconArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ===================== DICAS DE USO ===================== */}
      <section
        id="dicas"
        style={{ background: colors.dark, padding: "80px 24px" }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(24px, 3.2vw, 34px)",
              color: colors.white,
              margin: 0,
            }}
          >
            Dicas de uso e conservação da enceradeira industrial
          </h2>
          <p
            style={{
              color: colors.midGray,
              fontSize: 16,
              lineHeight: 1.7,
              maxWidth: 700,
              marginTop: 14,
            }}
          >
            Pequenos cuidados no dia a dia evitam a maior parte dos defeitos e
            aumentam significativamente a vida útil do equipamento. Confira as
            orientações da nossa equipe técnica de Brasília.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              marginTop: 40,
            }}
          >
            {dicas.map(({ icon: Icon, titulo, texto }) => (
              <div
                key={titulo}
                className="tip-card"
                style={{
                  background: colors.cardBg,
                  border: `1px solid rgba(255,255,255,0.06)`,
                  borderRadius: 16,
                  padding: 26,
                  transition: "all 0.3s ease",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: colors.glass,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  <Icon />
                </div>
                <h3
                  style={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    fontSize: 15.5,
                    color: colors.white,
                    margin: "0 0 8px",
                  }}
                >
                  {titulo}
                </h3>
                <p
                  style={{
                    color: colors.midGray,
                    fontSize: 14,
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  {texto}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FOTOS DOS SERVIÇOS ===================== */}
      <section style={{ background: colors.navy, padding: "80px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(24px, 3.2vw, 34px)",
              color: colors.white,
              margin: 0,
            }}
          >
            Serviços realizados em enceradeira industrial
          </h2>
          <p
            style={{
              color: colors.midGray,
              fontSize: 16,
              lineHeight: 1.7,
              maxWidth: 700,
              marginTop: 14,
            }}
          >
            Confira alguns dos atendimentos de conserto e manutenção de
            enceradeira industrial realizados pela nossa equipe em Brasília e
            região.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 18,
              marginTop: 36,
            }}
          >
            {fotosServicos.map(({ src, label, position }) => (
              <div key={src}>
                <PhotoBox
                  src={src}
                  alt={label}
                  label={label}
                  position={position}
                />
                <p
                  style={{
                    color: colors.midGray,
                    fontSize: 13.5,
                    marginTop: 10,
                    textAlign: "center",
                  }}
                >
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== ANTES E DEPOIS ===================== */}
      <section style={{ background: colors.dark, padding: "80px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(24px, 3.2vw, 34px)",
              color: colors.white,
              margin: 0,
            }}
          >
            Antes e depois: resultados reais
          </h2>
          <p
            style={{
              color: colors.midGray,
              fontSize: 16,
              lineHeight: 1.7,
              maxWidth: 700,
              marginTop: 14,
            }}
          >
            Veja a diferença que uma manutenção correta da enceradeira
            industrial faz no resultado final. Após uma manutenção detalhada e a
            renovação do equipamento, é possível recuperar seu desempenho,
            eficiência e acabamento.
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 48,
              marginTop: 40,
            }}
          >
            {antesDepois.map(
              ({ titulo, antes, antesPos, depois, depoisPos }) => (
                <div key={titulo}>
                  <h3
                    style={{
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 700,
                      fontSize: 17,
                      color: colors.white,
                      margin: "0 0 16px",
                    }}
                  >
                    {titulo}
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 18,
                    }}
                  >
                    <div>
                      <PhotoBox
                        src={antes}
                        alt={`Antes — ${titulo}`}
                        ratio="16 / 10"
                        label={`Antes — ${titulo}`}
                        position={antesPos}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          marginTop: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: colors.midGray,
                          border: `1px solid rgba(255,255,255,0.15)`,
                          borderRadius: 999,
                          padding: "4px 12px",
                        }}
                      >
                        Antes
                      </span>
                    </div>
                    <div>
                      <PhotoBox
                        src={depois}
                        alt={`Depois — ${titulo}`}
                        ratio="16 / 10"
                        label={`Depois — ${titulo}`}
                        position={depoisPos}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          marginTop: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: colors.white,
                          background: `linear-gradient(135deg, ${colors.blue}, ${colors.accent})`,
                          borderRadius: 999,
                          padding: "4px 12px",
                        }}
                      >
                        Depois
                      </span>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* ===================== CTA FINAL ===================== */}
      <section style={{ background: colors.navy, padding: "72px 24px" }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            background: `linear-gradient(135deg, ${colors.navyLight}, ${colors.cardBg})`,
            border: `1px solid rgba(0,180,216,0.2)`,
            borderRadius: 20,
            padding: "48px 40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 24,
          }}
        >
          <div style={{ maxWidth: 560 }}>
            <h3
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 800,
                fontSize: 24,
                color: colors.white,
                margin: "0 0 10px",
              }}
            >
              Precisa de assistência técnica para sua enceradeira industrial?
            </h3>
            <p
              style={{
                color: colors.midGray,
                fontSize: 15,
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              Solicite um orçamento sem compromisso. Atendemos Brasília,
              Taguatinga e todo o Distrito Federal.
            </p>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              background: `linear-gradient(135deg, ${colors.blue}, ${colors.accent})`,
              color: colors.white,
              textDecoration: "none",
              padding: "16px 30px",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
            }}
          >
            <IconWhatsApp size={18} /> Falar pelo WhatsApp
          </a>
        </div>
      </section>

      <Footer />

      <style>{`
        .tip-card:hover {
          background: ${colors.glassHover};
          border-color: rgba(0,180,216,0.25);
          transform: translateY(-3px);
        }
      `}</style>
    </div>
  );
};

export default PaginaEnceradeira;
